import React from 'react';
import DealValueFields from './sections/DealValueFields';
import EntityMultiSelect, { selectedEntityIds, entityLabel } from '../../../../shared/components/EntityMultiSelect';
import IconAction from '../../../../shared/components/IconAction';
import { dealFormSchema } from '../../config/dealForm.schema';
import { Plus } from '../../../../shared/assets/AppIcons';
import { Input } from '../../../../shared/ui/input';
import { Label } from '../../../../shared/ui/label';
import { Textarea } from '../../../../shared/ui/textarea';
import { getDealKindMeta, DEAL_TYPE_OPTIONS } from '../utils/dealAdapters';
import { formatCentsForInput } from '../../../../shared/utils/installments';
import ClientCustomFieldsSection from '../../../clients/components/ClientCustomFieldsSection';
import { archiveDealsCustomField, createDealsCustomField, listDealsCustomFields } from '../../services/deals-custom-fields.service';

import '../../styles/deal-create-form.css';

const DEAL_CUSTOM_FIELDS_API = {
  list: listDealsCustomFields, create: createDealsCustomField, archive: archiveDealsCustomField,
};
const PRIORITIES = [{ value: 'bassa', label: 'Bassa' }, { value: 'media', label: 'Media' }, { value: 'alta', label: 'Alta' }];

export default function DealFormFields({ dealType, formState, staff = [], clients = [], isSubmitting = false,
  staffCatalogStatus, updateField, handleClientSelect, onRequestNewClient, extraFields,
  addScadenza, updateScadenza, updateInstallmentAmount, beginInstallmentAmountEdit,
  commitInstallmentAmount, installmentDrafts = {}, removeScadenza,
}) {
  const meta = getDealKindMeta(dealType);
  const schema = dealFormSchema(dealType);
  const dueDateField = meta.scheduleDateField;
  const installmentCount = formState.dilazionamento.scadenze.length;
  const isStaffSelectionDisabled = staff.length === 0;
  const staffWarningMessage = staffCatalogStatus === 'unavailable'
    ? 'Il personale non è disponibile al momento. Riprova tra poco.'
    : 'Nessun membro del team disponibile nel catalogo.';
  return <>
      <div className="deal-create-form__row" data-deal-row="identity">
        <div data-form-field="name">
          <Label htmlFor="name">{schema.field('name').label} *</Label>
          <Input id="name" required value={formState.name} disabled={isSubmitting}
            onChange={(event) => updateField('name', event.target.value)} placeholder="Es. Fornitura server Enterprise Cloud" />
        </div>
        <div data-form-field="tipoAffare">
          <Label htmlFor="tipoAffare">{schema.field('tipoAffare').label}</Label>
          <select id="tipoAffare" value={formState.tipoAffare} disabled={isSubmitting} onChange={(event) => updateField('tipoAffare', event.target.value)}>
            <option value="">Seleziona tipo di affare…</option>
            {DEAL_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
      </div>

      <div data-form-field="description">
        <Label htmlFor="description">{schema.field('description').label}</Label>
        <Textarea id="description" value={formState.description} disabled={isSubmitting}
          onChange={(event) => updateField('description', event.target.value)} rows={3} maxLength={2000}
          placeholder={dealType === 'proposta'
            ? 'Descrivi i termini della proposta commerciale, i vantaggi e i prossimi passi…'
            : 'Descrivi i dettagli dell’affare, gli accordi e i prossimi passi…'} />
      </div>

      <fieldset className="deal-create-form__priority create-form-dialog__full">
        <legend>{dealType === 'proposta' ? 'Priorità' : 'Priorità dell’affare'}</legend>
        <div className="deal-create-form__priority-options">
          {PRIORITIES.map(({ value, label }) => <label key={value}>
            <input type="radio" name={meta.priorityField} value={value} checked={formState[meta.priorityField] === value}
              disabled={isSubmitting} onChange={() => updateField(meta.priorityField, value)} />
            <span>{label}</span>
          </label>)}
        </div>
      </fieldset>

      <div className="deal-create-form__row" data-deal-row="relationships">
        <div>
          <div className="deal-create-form__label-row">
            <Label htmlFor="selectClient">Cliente *</Label>
            {onRequestNewClient && <button type="button" className="deal-create-form__text-action" onClick={onRequestNewClient} disabled={isSubmitting}>Nuovo cliente</button>}
          </div>
          <EntityMultiSelect id="selectClient" label="Clienti" required options={clients} value={selectedEntityIds(formState, 'client')} disabled={isSubmitting}
            onChange={(ids) => { updateField('clientIds', ids); updateField('clientId', ids[0] || ''); handleClientSelect(ids[0] || ''); }} />
        </div>
        <div>
          <Label htmlFor="origineAffare">Origine (Segnalato da)</Label>
          <EntityMultiSelect id="origineAffare" label="Origine affare" options={staff.map((member) => ({ ...member, id: member.name }))}
            value={(formState.origineAffare || '').split('; ').filter(Boolean)}
            onChange={(names) => updateField('origineAffare', names.join('; '))} disabled={isStaffSelectionDisabled || isSubmitting} />
        </div>
      </div>

      <div className="deal-create-form__row" data-deal-row="assignment">
        <div>
          <Label htmlFor="assignee">Responsabile *</Label>
          <EntityMultiSelect id="assignee" label="Responsabili" required options={staff} value={selectedEntityIds(formState, 'staff')}
            disabled={isStaffSelectionDisabled || isSubmitting}
            onChange={(ids) => {
              updateField('staffIds', ids); updateField('staffId', ids[0] || '');
              updateField('assignee', ids.map((id) => entityLabel(staff.find((item) => String(item.id) === id))).join(', '));
            }} />
        </div>
        <div>
          <Label htmlFor={dueDateField}>Data di scadenza</Label>
          <Input id={dueDateField} type="date" value={formState[dueDateField]} disabled={isSubmitting}
            onChange={(event) => updateField(dueDateField, event.target.value)} />
        </div>
      </div>
      {isStaffSelectionDisabled ? <p role="alert" className="create-form-dialog__full wt-inline-error">{staffWarningMessage}</p> : null}

      <section className="deal-create-form__section" aria-labelledby="deal-value-heading">
        <h3 id="deal-value-heading" className="deal-create-form__section-title deal-create-form__section-title--accent">Valore e percentuali</h3>
        <DealValueFields deal={formState} amountField={meta.amountField} onFieldChange={updateField} compact disabled={isSubmitting} />
      </section>

      <section className="deal-create-form__section" aria-labelledby="deal-installments-heading">
        <h3 id="deal-installments-heading" className="deal-create-form__section-title">Dilazionamento</h3>
        <div className="deal-create-form__installments">
          {formState.dilazionamento.scadenze.map((scadenza, index) => {
            const isBalance = index === installmentCount - 1;
            return <div key={`installment-${index}`} className="deal-create-form__installment">
              <span className="deal-create-form__installment-label">Rata {index + 1}</span>
              <div className="deal-create-form__input-unit deal-create-form__input-unit--leading">
                <span aria-hidden="true">€</span>
                <Input inputMode="decimal" value={installmentDrafts[index] ?? formatCentsForInput(formState.dilazionamento.importiRate?.[index])}
                  readOnly={isBalance} disabled={isSubmitting}
                  aria-description={isBalance ? 'Saldo calcolato automaticamente' : undefined}
                  onFocus={() => { if (!isBalance) beginInstallmentAmountEdit(index); }}
                  onChange={(event) => updateInstallmentAmount(index, event.target.value)}
                  onBlur={() => commitInstallmentAmount(index)}
                  onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commitInstallmentAmount(index); } }}
                  placeholder="Importo rata…" aria-label={`Importo rata ${index + 1}`} />
              </div>
              <Input type="date" value={scadenza} disabled={isSubmitting} aria-label={`Data pagamento rata ${index + 1}`}
                onChange={(event) => updateScadenza(index, event.target.value)} />
              {installmentCount > 1 ? <div className="deal-create-form__remove-installment">
                <IconAction type="button" iconKey="actions.delete" tone="danger"
                  label={`Rimuovi rata ${index + 1}`} onClick={() => removeScadenza(index)} disabled={isSubmitting} />
              </div> : null}
            </div>;
          })}
        </div>
        {formState.dilazionamento.amountError ? <p className="wt-inline-error" role="alert">{formState.dilazionamento.amountError}</p> : null}
        <button type="button" className="deal-create-form__add-installment" onClick={addScadenza} disabled={isSubmitting}>
          <Plus size={16} aria-hidden="true" /> Aggiungi rata
        </button>
      </section>

      {extraFields}
      <ClientCustomFieldsSection formKey={schema.id} values={formState.customFields || {}}
        onChange={(customFields) => updateField('customFields', customFields)} api={DEAL_CUSTOM_FIELDS_API} />
  </>;
}
